// imports section
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Breadcrumbs,
  Link,
  AppBar,
  Checkbox,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  capitalize,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Navbar from "../../components/common/Navbar";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useStyles } from "../../assets/styles/Theme";
import {
  CON_STATUS_ACTIVE,
  CON_STATUS_DELETED,
} from "../../utils/sys/Constants";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/external/externalApis";
import {
  USERGROUP_API_01,
  USERGROUP_API_03,
  USERGROUP_API_04,
  USERGROUP_API_05,
  USERGROUP_API_06,
} from "../../api/ApiModel";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import {
  MSG_GROUPACCESS_SUCCESS,
  MSG_GROUPACCESS_FAILED,
} from "../../utils/sys/Messages";

const RichTreeViewWithCheckboxes = ({
  items,
  checkedItems,
  onCheckboxChange,
}) => {
  const [expanded, setExpanded] = useState({});
  const theme = useTheme();

  const handleToggle = (id) => {
    setExpanded((prevExpanded) => ({
      ...prevExpanded,
      [id]: !prevExpanded[id],
    }));
  };

  const handleCheckboxChange = (id) => {
    onCheckboxChange(id);
  };

  const isChecked = (subCode) => checkedItems.includes(subCode);

  return (
    <Box sx={{ paddingLeft: 2, minWidth: "100%" }}>
      {items.map((item) => (
        <Box
          key={item.id}
          sx={{
            marginBottom: 1,
            paddingLeft: item.level * 2,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ padding: "4px 8px", cursor: "pointer" }}
            onClick={() => handleToggle(item.id)}
          >
            {item.children && (
              <IconButton size="small" sx={{ marginRight: 1 }}>
                {expanded[item.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
            <Typography variant="body2" fontWeight="600">
              {item.label}
            </Typography>
          </Box>
          {item.children && (
            <Collapse in={expanded[item.id]} timeout="auto" unmountOnExit>
              {item.children.map((child) => (
                <Box
                  key={child.id}
                  sx={{
                    mx: 4,
                    display: "flex",
                    // justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={isChecked(child.id, child.GROUPCODE)}
                    onChange={() => handleCheckboxChange(child.id)}
                  />
                  <Typography variant="body2">{child.label}</Typography>
                </Box>
              ))}
            </Collapse>
          )}
        </Box>
      ))}
    </Box>
  );
};

const MapPrivileges = () => {
  // Hooks
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = useStyles(theme);

  const [userGroups, setUserGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [mappedPriviledges, setMappedPriviledges] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedModule, setSelectedModule] = useState("");

  const [accessRights, setAccessRights] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, GROUPCODE, USERNAME, RESTCODE, USERREFCODE } =
    SYS_USER.USER_PARAM || {};

  useEffect(() => {
    fetchUserGroups();
    // updateUserAccess();
    // fetchModules();
  }, []);

  useEffect(() => {
    if (selectedGroup && selectedModule) {
      fetchUserAccess(selectedGroup, selectedModule);
    }
  }, [selectedGroup, selectedModule]);

  useEffect(() => {
    if (selectedGroup) {
      fetchUserAccessForModules(selectedGroup);
    }
  }, [selectedGroup]);

  // Handlers

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // API calls

  // Fetch user groups from API
  const fetchUserGroups = async () => {
    setLoading(true);
    try {
      const params = {
        COMPCODE: COMPCODE,
        STS: CON_STATUS_ACTIVE,
      };
      const response = await externalgetApi(USERGROUP_API_01, "GET", params);
      setUserGroups(response); // Assuming response is an array of user groups

      console.log("fetchUserGroups", response);
    } catch (error) {
      setError("Failed to fetch user groups.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch modules
  // const fetchModules = async () => {
  //   try {
  //     setLoading(true);
  //     const params = {
  //       COMPCODE: COMPCODE,
  //       STS: CON_STATUS_ACTIVE,
  //     };
  //     const response = await externalgetApi(USERGROUP_API_05, "GET", params);
  //     setModules(response);

  //     console.log("fetchModules", response);
  //   } catch (error) {
  //     setError("Failed to fetch user groups.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Fetch access rights data based on selected group
  const fetchUserAccess = async (groupCode, moduleCode) => {
    try {
      setLoading(true);
      const response = await externalgetApi(USERGROUP_API_04, "GET", {
        GROUPCODE: groupCode,
        // MODULECODE: moduleCode,
        COMPCODE: COMPCODE,
      });

      console.log("fetch user access", response);
      setAccessRights(response);

      // Transform data for TreeView
      const formattedData = transformUserAccessData(response, moduleCode);
      setTreeData(formattedData);
      console.log("response fetch user access", formattedData);
    } catch (error) {
      setError("Failed to fetch user access rights.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAccessForModules = async (groupCode) => {
    try {
      setLoading(true);
      const response = await externalgetApi(USERGROUP_API_04, "GET", {
        GROUPCODE: groupCode,
        COMPCODE: COMPCODE,
      });

      // Extract unique MODULECODE and MODULENAME
      const uniqueModules = response.reduce((acc, curr) => {
        if (!acc.some((item) => item.MODULECODE === curr.MODULECODE)) {
          acc.push({
            MODULECODE: curr.MODULECODE,
            MODULENAME: curr.MODULENAME,
          });
        }
        return acc;
      }, []);

      setModules(uniqueModules);
      console.log("Modules for dropdown", uniqueModules);
    } catch (error) {
      setError("Failed to fetch user access for modules.");
    } finally {
      setLoading(false);
    }
  };

  const transformUserAccessData = (data, moduleCode) => {
    return data.reduce((acc, item) => {
      if (item.MODULECODE !== moduleCode) return acc;

      const mainMenu = acc.find((menu) => menu.label === item.MAINMENU);
      if (mainMenu) {
        mainMenu.children.push({
          id: item.SUBCODE,
          label: item.SUBMENU,
          url: item.URL,
          GROUPCODE: item.GROUPCODE, // Ensure GROUPCODE is included
        });
      } else {
        acc.push({
          id: item.MAINCODE,
          label: item.MAINMENU,
          children: [
            {
              id: item.SUBCODE,
              label: item.SUBMENU,
              url: item.URL,
              GROUPCODE: item.GROUPCODE, // Ensure GROUPCODE is included
            },
          ],
        });
      }
      return acc;
    }, []);
  };

  const handleGroupChange = (event) => {
    setSelectedGroup(event.target.value);
    setSelectedModule(""); // Reset module selection
  };

  const handleModuleChange = (event) => {
    const moduleCode = event.target.value;
    setSelectedModule(moduleCode);

    if (selectedGroup && moduleCode) {
      fetchUserAccess(selectedGroup, moduleCode); // Fetch privileges for selected module
    }
  };

  useEffect(() => {
    if (treeData.length > 0) {
      // Automatically check items not in GROUPCODE === "0"
      const initialChecked = treeData.flatMap((item) =>
        item.children
          .filter((child) => child.GROUPCODE !== "0")
          .map((child) => child.id)
      );
      setCheckedItems(initialChecked);
    }
  }, [treeData]);

  const handleCheckboxChange = (subCode) => {
    setCheckedItems(
      (prev) =>
        prev.includes(subCode)
          ? prev.filter((item) => item !== subCode) // Uncheck
          : [...prev, subCode] // Check
    );
  };

  // Check if mapped privileges are present to set the initial checked state
  // Check and set initial checkedItems based on mapped privileges for the selectedModule
  // Check and set initial checkedItems based on mapped privileges for the selectedModule
  useEffect(() => {
    if (mappedPriviledges.length > 0) {
      const initialCheckedItems = mappedPriviledges
        .filter(
          (priv) => priv.MODULECODE === selectedModule && priv.GROUPCODE !== "0" // Exclude auto-checked items
        )
        .map((priv) => priv.SUBCODE);
      setCheckedItems(initialCheckedItems);
    }
  }, [mappedPriviledges, selectedModule]);

  const handleSubmit = async () => {
    try {
      // Prepare payload
      const payload = {
        mapmodulesDt: checkedItems.map((subcode) => {
          // Find the matching maincode for the subcode from accessRights
          const matchingAccess = accessRights.find(
            (item) => item.SUBCODE === subcode
          );
          return {
            modulecode: String(selectedModule),
            addoncode: matchingAccess?.ADDONCODE
              ? String(matchingAccess.ADDONCODE)
              : "",
            maincode: String(matchingAccess?.MAINCODE || ""), // Ensure maincode is a string
            subcode: String(subcode), // Ensure subcode is a string
            sts: CON_STATUS_ACTIVE,
          };
        }),
        compcode: COMPCODE,
        groupcode: selectedGroup,

        enuser: USERNAME,
      };

      // Log payload to verify data
      console.log("post payload", payload);

      // Make API call
      const response = await externalpostApi(USERGROUP_API_06, payload);

      // Log response and alert success
      console.log("post response", response);
      setSnackbarMessage(MSG_GROUPACCESS_SUCCESS);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      // Handle error
      setError("Failed to update access mapping.");
      console.error("API call error:", error);
    }
  };

  return (
    <>
      <Navbar />

      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                // position="fixed"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Employee</Link>
                <Link underline="none">Group Access</Link>
              </Breadcrumbs>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.box_container_add}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Box sx={styles.col_title}>
              <Typography variant="title" align="left">
                Map Privileges
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={5} lg={5} xl={5}>
            <Box
              sx={{
                ...styles.col_1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "flex-start",
                textAlign: "left",
                px: 2,
                marginLeft: 0,
                maxHeight: "68vh",
                height: {
                  xs: "auto",
                  sm: "auto",
                  md: "100vh",
                  lg: "100vh",
                  xl: "100vh",
                },
                boxShadow: 1,
                borderRadius: 1,
                py: 3,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="group-select-label">User Group</InputLabel>
                <Select
                  labelId="group-select-label"
                  id="group-select"
                  label="User Group"
                  value={selectedGroup}
                  onChange={handleGroupChange}
                >
                  {userGroups.map((group) => (
                    <MenuItem key={group.GROUPCODE} value={group.GROUPCODE}>
                      {group.GROUPNAME}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box mt={2} width={"100%"}>
                <FormControl fullWidth size="small">
                  <InputLabel id="group-select-label">Modules</InputLabel>
                  <Select
                    labelId="group-select-label"
                    id="group-select"
                    label="User Group"
                    value={selectedModule}
                    onChange={handleModuleChange}
                  >
                    {modules.map((module) => (
                      <MenuItem
                        key={module.MODULECODE}
                        value={module.MODULECODE}
                      >
                        {module.MODULENAME}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-end",
                  marginTop: 2,
                  width: "20%",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!selectedGroup || !selectedModule || loading}
                  sx={styles.btn_primary}
                >
                  Confirm
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={7} lg={7} xl={7}>
            <Box
              sx={{
                // ...styles.col_1,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                textAlign: "left",
                borderRadius: 1,
                p: 1,
                // py: 1,
                maxHeight: "68vh",
                overflow: "auto",
                height: "100vh",
                boxShadow: 1,
              }}
            >
              {treeData.length > 0 && (
                <RichTreeViewWithCheckboxes
                  items={treeData}
                  checkedItems={checkedItems}
                  onCheckboxChange={handleCheckboxChange}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <SnackbarMessage
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default MapPrivileges;
